import React, { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Download, FileText, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Button from '../../../components/common/Button/Button';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : ''
  };
};

const formatNumber = (value) => {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toString() : number.toFixed(2).replace(/\.?0+$/, '');
};

const LeaveBalanceImportModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [validation, setValidation] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [step, setStep] = useState(1);
  const [isParsing, setIsParsing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const normalizeRows = (rows) => rows
    .map((row) => {
      const cleaned = {};
      Object.keys(row || {}).forEach((key) => {
        cleaned[String(key).trim()] = row[key] === null || row[key] === undefined ? '' : String(row[key]).trim();
      });
      return cleaned;
    })
    .filter((row) => Object.values(row).some((value) => String(value).trim() !== ''));

  const validateRows = async (rows, selectedFile = file) => {
    setIsValidating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/leave-management/balance-import/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ data: rows, fileName: selectedFile?.name || '' })
      });
      const result = await response.json();
      if (response.ok && (result.success || result.responseCode === 200)) {
        setValidation(result.data);
        setStep(2);
      } else {
        toast.error(result.responseMessage || result.message || 'Validation failed');
      }
    } catch (error) {
      toast.error('Unable to validate leave balance file');
    } finally {
      setIsValidating(false);
    }
  };

  const parseCsv = (selectedFile) => {
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = normalizeRows(result.data || []);
        if (!rows.length) {
          toast.error('File has no data rows');
          setIsParsing(false);
          return;
        }
        setParsedRows(rows);
        setIsParsing(false);
        validateRows(rows, selectedFile);
      },
      error: () => {
        toast.error('Unable to parse CSV file');
        setIsParsing(false);
      }
    });
  };

  const parseExcel = (selectedFile) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = normalizeRows(XLSX.utils.sheet_to_json(worksheet, { defval: '' }));
        if (!rows.length) {
          toast.error('File has no data rows');
          setIsParsing(false);
          return;
        }
        setParsedRows(rows);
        setIsParsing(false);
        validateRows(rows, selectedFile);
      } catch (error) {
        toast.error('Unable to parse Excel file');
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      toast.error('Unable to read file');
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error('Please upload CSV, XLSX, or XLS file');
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setValidation(null);
    setUploadResult(null);
    setIsParsing(true);

    if (selectedFile.name.match(/\.csv$/i)) parseCsv(selectedFile);
    else parseExcel(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/leave-management/balance-import/template`, {
        headers: { Authorization: getAuthHeaders().Authorization }
      });
      if (!response.ok) throw new Error('Template download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'leave_balance_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Unable to download template');
    }
  };

  const handleConfirmImport = async () => {
    if (!parsedRows.length || validation?.invalid > 0) return;
    setIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/leave-management/balance-import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          data: parsedRows,
          batchId: validation?.batchId,
          fileName: file?.name || ''
        })
      });
      const result = await response.json();
      if (response.ok && (result.success || result.responseCode === 201 || result.responseCode === 200)) {
        setUploadResult(result.data);
        setStep(3);
        toast.success('Leave balances imported successfully');
      } else {
        setValidation(result.data || validation);
        toast.error(result.responseMessage || result.message || 'Import failed');
      }
    } catch (error) {
      toast.error('Unable to import leave balances');
    } finally {
      setIsUploading(false);
    }
  };

  const readyRows = validation?.valid || validation?.validRows || 0;
  const errorRows = validation?.invalid || validation?.failed || 0;
  const previewRows = validation?.previewRows || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Import Leave Balances</h3>
            <p className="text-sm text-gray-500 mt-0.5">Validate legacy HRMS balances before writing them into HRMS.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          {step === 1 && (
            <div className="max-w-4xl mx-auto space-y-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Download className="h-5 w-5 text-[#0F2942]" />
                    <span className="font-semibold text-gray-900">Download Template</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Use Policy ID values like LP001 from the Leave Policies table.</p>
                  <Button variant="outline" size="sm" icon={<Download className="h-4 w-4" />} onClick={handleDownloadTemplate}>
                    Download CSV Template
                  </Button>
                </div>
                <div className="bg-white p-5 rounded-xl border border-amber-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-gray-900">Validation Rules</span>
                  </div>
                  <ul className="text-xs text-gray-600 space-y-1 list-disc pl-4">
                    <li>Employee ID, Policy ID, Year, Entitlement, Accrued, Used, Pending, and Available are required.</li>
                    <li>Remarks is optional.</li>
                    <li>Available must match the policy formula exactly.</li>
                    <li>Rows with errors block the full import.</li>
                  </ul>
                </div>
              </div>

              <div
                className="border-2 border-dashed rounded-2xl p-12 text-center bg-white hover:bg-blue-50/20 hover:border-blue-400 transition-colors"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  handleFile(event.dataTransfer.files[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(event) => handleFile(event.target.files[0])}
                />
                {isParsing || isValidating ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                    <p className="text-lg font-medium text-gray-900">{isParsing ? 'Reading file...' : 'Validating balances...'}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                      <Upload className="h-8 w-8 text-[#0F2942]" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload CSV or Excel File</h4>
                    <p className="text-sm text-gray-500 mb-6">Supported formats: .csv, .xlsx, .xls</p>
                    <Button variant="primary" onClick={() => fileInputRef.current?.click()} className="bg-[#0F2942] hover:bg-[#0B2033] px-8">
                      Select File
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                  <div className="text-2xl font-bold text-gray-900">{validation?.total || 0}</div>
                  <div className="text-xs uppercase font-semibold text-gray-500">Total Rows</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                  <div className="text-2xl font-bold text-green-600">{readyRows}</div>
                  <div className="text-xs uppercase font-semibold text-gray-500">Ready</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                  <div className="text-2xl font-bold text-red-600">{errorRows}</div>
                  <div className="text-xs uppercase font-semibold text-gray-500">Errors</div>
                </div>
              </div>

              {errorRows > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex gap-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Import is blocked until all calculation and mapping errors are fixed.</div>
                    <div>Download/correct the source file, then upload again. The system will not auto-correct balances.</div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">Validation Preview</span>
                </div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        {['Row', 'Status', 'Employee ID', 'Policy ID', 'Year', 'Entitlement', 'Accrued', 'Used', 'Pending', 'Available', 'Remarks', 'Details'].map((header) => (
                          <th
                            key={header}
                            className={`px-3 py-2 font-bold text-gray-500 uppercase border-b whitespace-nowrap ${header === 'Policy ID' ? 'min-w-[110px]' : ''}`}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {previewRows.map((row) => (
                        <tr key={row.row} className={row.status === 'error' ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2">{row.row}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded-full font-semibold ${row.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {row.status === 'error' ? 'Error' : 'Ready'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.employeeId}</td>
                          <td className="px-3 py-2 font-mono whitespace-nowrap min-w-[110px]">{row.policyId}</td>
                          <td className="px-3 py-2">{row.year}</td>
                          <td className="px-3 py-2">{formatNumber(row.entitlement)}</td>
                          <td className="px-3 py-2">{formatNumber(row.accrued)}</td>
                          <td className="px-3 py-2">{formatNumber(row.used)}</td>
                          <td className="px-3 py-2">{formatNumber(row.pending)}</td>
                          <td className="px-3 py-2">{formatNumber(row.available)}</td>
                          <td className="px-3 py-2 max-w-[180px] truncate">{row.remarks || '-'}</td>
                          <td className="px-3 py-2 min-w-[320px]">
                            {row.errors?.length ? (
                              <ul className="list-disc pl-4 text-red-700 space-y-1">
                                {row.errors.map((error, index) => <li key={index}>{error}</li>)}
                              </ul>
                            ) : (
                              <span className="text-green-700">Ready to import</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-12 max-w-md mx-auto">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Leave Balances Imported</h4>
              <p className="text-sm text-gray-500 mb-6">Batch ID: {uploadResult?.batchId}</p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-green-50 p-3 rounded-xl border border-green-100">
                  <div className="text-xl font-bold text-green-600">{uploadResult?.created || 0}</div>
                  <div className="text-[10px] font-semibold text-green-700 uppercase">Created</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-xl border border-blue-100">
                  <div className="text-xl font-bold text-[#0F2942]">{uploadResult?.updated || 0}</div>
                  <div className="text-[10px] font-semibold text-[#0B2033] uppercase">Updated</div>
                </div>
                <div className="bg-red-50 p-3 rounded-xl border border-red-100">
                  <div className="text-xl font-bold text-red-600">{uploadResult?.failed || 0}</div>
                  <div className="text-[10px] font-semibold text-red-700 uppercase">Failed</div>
                </div>
              </div>
              <Button variant="primary" onClick={() => { onSuccess?.(); onClose(); }} className="w-full bg-[#0F2942] hover:bg-[#0B2033]">
                Return to Leave Policies
              </Button>
            </div>
          )}
        </div>

        {step !== 3 && (
          <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-between items-center">
            <Button variant="outline" onClick={step === 2 ? () => setStep(1) : onClose} disabled={isUploading || isParsing || isValidating}>
              {step === 2 ? 'Upload Another File' : 'Cancel'}
            </Button>
            {step === 2 && (
              <Button
                variant="primary"
                onClick={handleConfirmImport}
                disabled={isUploading || isValidating || errorRows > 0 || readyRows === 0}
                className="bg-[#0F2942] hover:bg-[#0B2033] px-8"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Importing...</span>
                ) : 'Confirm Import'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveBalanceImportModal;
